/**
 * OptionDeskPanel — the AI's option recommendations for one underlying.
 *
 * The information hierarchy is deliberate and it is the opposite of what a returns-first layout
 * would give you:
 *
 *   1. THE READ      what regime the model thinks this is, and the volatility stance that follows.
 *                    Everything below is downstream of getting this right.
 *   2. THE TRADES    concrete structures, with EXPECTANCY and probability shown beside the headline
 *                    return rather than beneath it. A live BTC run produced a 474% return on a
 *                    structure with a 21.8% chance of paying and a negative expected payoff; a
 *                    layout that leads with ROI would have sold exactly that trade.
 *   3. ALSO SEEN     what was built and rejected, so "why not the other one?" has an answer.
 *
 * Unbounded loss is never rendered as a number. It says UNBOUNDED, in the loss colour, because a
 * large figure and no figure at all are different things and only one of them can wipe an account.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Button, Divider, Tooltip, CircularProgress, Collapse,
  TextField, MenuItem, LinearProgress, Alert as MuiAlert,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppTheme } from '../../hooks/useAppTheme';
import aiService, { type AiOptionRecommendation, type AiOptionCandidate } from '../../services/ai.service';
import { useAiCost, costLabel, inr, refreshAiCost } from '../../hooks/useAiCost';
import HelpLink from '../common/HelpLink';
import AiRangePanel from './AiRangePanel';
import toast from 'react-hot-toast';

const UNDERLYINGS = ['BTC', 'ETH'];

const STANCE_LABEL: Record<string, string> = {
  SELL_PREMIUM: 'Sell premium',
  BUY_PREMIUM: 'Buy premium',
  NEUTRAL_VOL: 'No volatility edge',
};

const OptionDeskPanel: React.FC = () => {
  const { C } = useAppTheme();
  const { report: cost, disabled: aiOff, capReached, unavailableReason } = useAiCost();
  const [symbol, setSymbol] = useState('BTC');
  const [rec, setRec] = useState<AiOptionRecommendation | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showConsidered, setShowConsidered] = useState(false);
  const [showRead, setShowRead] = useState(false);

  const load = useCallback(async (sym: string) => {
    setLoading(true);
    try {
      const r = await aiService.getOptionRecommendations(sym);
      setRec((r as AiOptionRecommendation) || null);
    } catch { setRec(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(symbol); }, [symbol, load]);

  const run = async (dryRun: boolean) => {
    setRunning(true);
    try {
      toast.success(dryRun
        ? 'Building structures from the live chain — no model calls.'
        : 'Reading the regime and pricing structures. This takes a couple of minutes.');
      await aiService.runOptionDesk({ symbol, dryRun });
      await load(symbol);
      // The estimate is a trailing median, so the run that just happened belongs in it.
      if (!dryRun) refreshAiCost();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'The option desk run failed');
    } finally { setRunning(false); }
  };

  const act = async (action: 'WATCHED' | 'DISMISSED' | 'BUILT') => {
    if (!rec) return;
    try {
      const updated = await aiService.setOptionRecommendationAction(
        rec.id, rec.humanAction === action ? 'NONE' : action);
      setRec({ ...rec, humanAction: updated.humanAction });
    } catch { toast.error('Could not record that'); }
  };

  const outlook = rec?.outlook || null;
  const ranking = rec?.ranking || null;
  const byRef = new Map((rec?.candidates || []).map(c => [c.ref, c]));
  const rankedRefs = new Set((ranking?.ranked || []).map(r => r.ref));

  const recommended = (ranking?.ranked || [])
    .filter(r => r.verdict !== 'REJECT' && byRef.has(r.ref))
    .sort((a, b) => a.rank - b.rank);
  const considered = (rec?.candidates || []).filter(c => !rankedRefs.has(c.ref)
    || (ranking?.ranked || []).some(r => r.ref === c.ref && r.verdict === 'REJECT'));

  const money = (v: number | null | undefined) =>
    v == null ? '—' : `₹${Math.round(v).toLocaleString('en-IN')}`;

  return (
    <Box>
      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField select size="small" label="Underlying" value={symbol}
          onChange={e => setSymbol(e.target.value)} sx={{ minWidth: 130 }}>
          {UNDERLYINGS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
        </TextField>
        <Tooltip arrow title="Prices and ranks concrete structures deterministically, with no model call. Costs nothing.">
          <span>
            <Button size="small" variant="outlined" startIcon={<ScienceIcon />}
              disabled={running} onClick={() => run(true)}>
              Build only · ₹0
            </Button>
          </span>
        </Tooltip>
        <Tooltip arrow title={
          aiOff || capReached ? unavailableReason : (
            <span>
              Two model calls — a regime read, then a ranking of the priced candidates.
              {cost?.perAction.optionDesk != null && (
                <>
                  <br /><br />
                  <b>Typical cost {inr(cost.perAction.optionDesk)}</b>, measured from real runs
                  {cost.model.selected && <> on {cost.model.selected}</>}.
                  <br />Spent today {inr(cost.guard.spentTodayUsd)} of {inr(cost.guard.capUsd)}.
                </>
              )}
            </span>
          )
        }>
          <span>
            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
              disabled={running || aiOff || capReached} onClick={() => run(false)}>
              {aiOff ? 'AI is off' : capReached ? 'Cap reached'
                : `Ask the desk${costLabel(cost?.perAction.optionDesk) ? ` · ${costLabel(cost?.perAction.optionDesk)!.replace('≈ ', '')}` : ''}`}
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {rec && (
          <Typography variant="caption" sx={{ color: C.dim }}>
            {new Date(rec.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
            {rec.costUsd ? ` · $${Number(rec.costUsd).toFixed(3)}` : ''}
            {rec.promptVersion ? ` · ${rec.promptVersion}` : ''}
          </Typography>
        )}
      </Box>

      {running && <LinearProgress sx={{ mb: 2 }} />}
      {loading && !rec && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress size={24} /></Box>}

      {!loading && !rec && (
        <MuiAlert severity="info" variant="outlined">
          No recommendation for {symbol} yet. <b>Build only</b> prices every structure from the live
          chain with no model calls and no spend. <b>Ask the desk</b> does that and then has the model
          read the regime and rank them.
        </MuiAlert>
      )}

      {rec?.status === 'NO_DATA' && (
        <MuiAlert severity="warning">{rec.note || 'No usable option chain for this underlying.'}</MuiAlert>
      )}
      {rec?.status === 'FAILED' && (
        <MuiAlert severity="error">{rec.note || 'The run failed.'}</MuiAlert>
      )}
      {rec?.status === 'NO_TRADE' && (
        <MuiAlert severity="info" sx={{ mb: 2 }}>
          <b>Nothing here is worth the capital.</b> {rec.note}
        </MuiAlert>
      )}

      {/* ── 1. The read ──────────────────────────────────────────────────── */}
      {outlook && (
        <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: C.neutralMuted, border: `1px solid ${C.neutralBorder}` }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.8 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>The read</Typography>
            <HelpLink to="ai-desk/options" variant="icon" label="How to read the Option Desk" />
            <Chip size="small" label={outlook.direction} sx={{
              height: 20, fontWeight: 800,
              bgcolor: outlook.direction === 'LONG' ? C.bullMuted : outlook.direction === 'SHORT' ? C.bearMuted : C.dimMuted,
              color: outlook.direction === 'LONG' ? C.bull : outlook.direction === 'SHORT' ? C.bear : C.dim,
            }} />
            <Tooltip arrow title="The volatility decision is separate from the direction one and at least as important: a correct direction expressed with the wrong volatility stance still loses money.">
              <Chip size="small" label={STANCE_LABEL[outlook.volStance] || outlook.volStance}
                sx={{ height: 20, fontWeight: 700, bgcolor: C.accentMuted, color: C.accent, cursor: 'help' }} />
            </Tooltip>
            <Typography variant="caption" sx={{ color: C.dim }}>conviction {outlook.conviction}</Typography>
            <Typography variant="caption" sx={{ color: C.dim }}>
              · expiry window {outlook.expiryMinDays}–{outlook.expiryMaxDays} days
            </Typography>
          </Box>
          {/* Clamped until asked for, like the Signals cards. The read is a single dense
              paragraph — genuinely worth reading, but not as the opening move every morning, and
              the eight labelled parts below say the same things in a form that can be scanned. */}
          <Typography variant="body2" sx={{
            lineHeight: 1.5,
            ...(showRead ? {} : {
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }),
          }}>
            {outlook.summary}
          </Typography>

          <Button size="small" sx={{ mt: 0.5, fontWeight: 700, textTransform: 'none' }}
            onClick={() => setShowRead(v => !v)}
            startIcon={showRead ? <ExpandMoreIcon /> : <ChevronRightIcon />}>
            {showRead ? 'Hide the reasoning' : 'Why — volatility, structure, expiry, gamma, risk'}
          </Button>
          <Collapse in={showRead}>
            <Box sx={{ display: 'grid', gap: 0.8, mt: 0.5 }}>
              {[
                ['Volatility', outlook.volReasoning],
                ['Already-priced move', outlook.expectedMoveComment],
                ['Structure', outlook.structureReasoning],
                ['Expiry', outlook.expiryReasoning],
                ['Gamma', outlook.gammaRiskComment],
                ['Your book', outlook.portfolioFitComment],
                ['Main risk', outlook.keyRisk],
                ['Invalidated if', outlook.invalidation],
              ].map(([k, v]) => v ? (
                <Typography key={k as string} variant="caption" sx={{ display: 'block' }}>
                  <b>{k}:</b> {v}
                </Typography>
              ) : null)}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ── 2. The trades ────────────────────────────────────────────────── */}
      {!!recommended.length && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            On your plate ({recommended.length})
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
            {recommended.map(r => {
              const c = byRef.get(r.ref)!;
              return <TradeCard key={r.ref} c={c} why={r.why} risks={r.risks}
                verdict={r.verdict} rank={r.rank} money={money} />;
            })}
          </Box>
        </>
      )}

      {ranking?.overallComment && (
        <Typography variant="caption" sx={{ display: 'block', color: C.dim, mb: 2 }}>
          {ranking.overallComment}
        </Typography>
      )}

      {rec && rec.status !== 'FAILED' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button size="small" variant={rec.humanAction === 'WATCHED' ? 'contained' : 'outlined'}
            onClick={() => act('WATCHED')}>Watch</Button>
          <Button size="small" color="inherit"
            variant={rec.humanAction === 'DISMISSED' ? 'contained' : 'outlined'}
            onClick={() => act('DISMISSED')}>Dismiss</Button>
          <Button size="small" variant={rec.humanAction === 'BUILT' ? 'contained' : 'outlined'}
            onClick={() => act('BUILT')}>Mark as built</Button>
        </Box>
      )}

      {/* ── 3. Also considered ───────────────────────────────────────────── */}
      {!!considered.length && (
        <Box>
          <Button size="small" onClick={() => setShowConsidered(v => !v)} sx={{ fontWeight: 800 }}
            startIcon={showConsidered ? <ExpandMoreIcon /> : <ChevronRightIcon />}>
            Also built and priced ({considered.length})
          </Button>
          <Collapse in={showConsidered}>
            <Typography variant="caption" sx={{ display: 'block', color: C.dim, mb: 1 }}>
              Every structure the builder produced from the live chain. Kept so that &quot;why not that
              one?&quot; has an answer, and so the ranking can be scored later against what the market did.
            </Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {considered.map(c => {
                const rj = (ranking?.ranked || []).find(r => r.ref === c.ref);
                return <TradeCard key={c.ref} c={c} why={rj?.why} risks={rj?.risks}
                  verdict={rj?.verdict} rank={rj?.rank} money={money} compact />;
              })}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ── 4. How far it is likely to move ──────────────────────────────────
          Moved here from the Edge AI page. Strike selection, expiry choice and every break-even
          judgement above are the same question these bands answer, and it was two clicks and a
          different mental model away. Unlike a ranked pick a band reports its own measured
          coverage, which is why this part of that engine survived the merge. */}
      <AiRangePanel />
    </Box>
  );
};

/** One structure, with expectancy shown beside the headline return rather than beneath it. */
/**
 * Rupees, keeping the precision a figure actually has.
 *
 * The panel's `money` rounds to whole rupees, which is right for a margin or a max loss and
 * destructive for an expectancy: the live board rendered "₹1" and "₹0" on two cards whose own
 * prose quoted 0.69 and 0.21. On a single lot a sub-rupee expected payoff is the normal case, and
 * the sign and magnitude of it are the whole point of the figure.
 */
function fineMoney(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a === 0) return '₹0';
  if (a < 10) return `${v < 0 ? '-' : ''}₹${a.toFixed(2)}`;
  if (a < 1000) return `${v < 0 ? '-' : ''}₹${a.toFixed(1).replace(/\.0$/, '')}`;
  return `${v < 0 ? '-' : ''}₹${Math.round(a).toLocaleString('en-IN')}`;
}

const TradeCard: React.FC<{
  c: AiOptionCandidate;
  why?: string; risks?: string[];
  verdict?: string; rank?: number;
  money: (v: number | null | undefined) => string;
  compact?: boolean;
}> = ({ c, why, risks, verdict, rank, money, compact }) => {
  const { C } = useAppTheme();
  const negEv = c.expectedPayoff != null && c.expectedPayoff < 0;
  const border = verdict === 'PREFERRED' ? C.bull : verdict === 'REJECT' ? C.dim : C.neutral;

  const Fig: React.FC<{ label: string; value: string; color?: string; hint?: string }> =
    ({ label, value, color, hint }) => {
      const body = (
        <Box sx={{ minWidth: 92 }}>
          <Typography variant="caption" sx={{ color: C.dim, display: 'flex', alignItems: 'center', gap: 0.3 }}>
            {label}{hint && <InfoOutlinedIcon sx={{ fontSize: 11 }} />}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: color || 'inherit', fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </Typography>
        </Box>
      );
      return hint ? <Tooltip arrow title={hint}>{body}</Tooltip> : body;
    };

  return (
    <Box sx={{
      p: 1.5, borderRadius: 1, border: `1px solid ${border}55`,
      borderLeft: `4px solid ${border}`, bgcolor: C.neutralMuted,
      opacity: verdict === 'REJECT' ? 0.65 : 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {rank != null && verdict !== 'REJECT' && (
          <Chip size="small" label={`#${rank}`} sx={{ height: 20, fontWeight: 800 }} />
        )}
        <Typography sx={{ fontWeight: 800 }}>{c.name}</Typography>
        <Chip size="small" label={`${c.expiry} · ${c.daysToExpiry}d`} sx={{ height: 19, fontWeight: 700 }} />
        <Chip size="small" label={c.riskShape} sx={{ height: 19, fontSize: '0.62rem', fontWeight: 700 }} />
        {verdict && (
          <Chip size="small" label={verdict} sx={{
            height: 19, fontSize: '0.62rem', fontWeight: 800,
            bgcolor: verdict === 'PREFERRED' ? C.bullMuted : verdict === 'REJECT' ? C.dimMuted : C.neutralMuted,
            color: verdict === 'PREFERRED' ? C.bull : verdict === 'REJECT' ? C.dim : C.neutral,
          }} />
        )}
        {c.lossUnbounded && (
          <Tooltip arrow title="This structure has NO maximum loss. With size, that is the difference between a bad day and a wiped account.">
            <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: 13 }} />} label="Unbounded loss"
              sx={{ height: 19, fontSize: '0.62rem', fontWeight: 800, bgcolor: C.bearMuted, color: C.bear }} />
          </Tooltip>
        )}
        {negEv && (
          <Tooltip arrow title="Probability-weighted payoff is negative: across the market's own implied distribution this structure loses money on average, however attractive the headline return looks.">
            <Chip size="small" label="Negative expectancy"
              sx={{ height: 19, fontSize: '0.62rem', fontWeight: 800, bgcolor: C.bearMuted, color: C.bear }} />
          </Tooltip>
        )}
      </Box>

      {/* Legs, exactly as they would be executed.

          The premiums are per unit of the underlying in USD and every figure below is in rupees
          for the whole position, so the two lines look unrelated until you know the lot size: a
          BTC option is 0.001 BTC, which is why "@ 868.47" and a max loss of Rs 153 are the same
          trade. The card used to print both and explain neither. */}
      <Tooltip arrow title={
        'Premiums are quoted per unit of the underlying, in USD, exactly as the exchange shows ' +
        'them. Every rupee figure below is for the whole position — premium x lot size x quantity, ' +
        'converted. That is why a premium in the hundreds produces a max loss in the hundreds of ' +
        'rupees rather than the hundreds of thousands.'
      }>
        <Typography variant="caption" sx={{
          display: 'block', mt: 0.7, fontFamily: 'monospace', color: C.dim, cursor: 'help',
        }}>
          {c.legs.map(l => `${l.action} ${l.quantity}× ${l.strike}${l.optionType} @ ${l.ltp}`).join('   ')}
          <Typography component="span" variant="caption" sx={{ fontFamily: 'inherit', opacity: 0.8 }}>
            {'   '}(premiums per unit, USD)
          </Typography>
        </Typography>
      </Tooltip>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <Fig label="Max profit" color={C.bull}
          value={c.profitUnbounded ? 'Unbounded' : money(c.maxProfit)} />
        <Fig label="Max loss" color={C.bear}
          value={c.lossUnbounded ? 'UNBOUNDED' : money(c.maxLoss)} />
        <Fig label="Margin" value={money(c.marginEstimate)}
          hint={c.marginSource === 'BROKER' ? 'Real broker SPAN margin.' : 'In-app estimate, not a broker quote.'} />
        <Fig label="Chance of profit" value={c.probabilityOfProfit == null ? '—' : `${c.probabilityOfProfit}%`}
          color={(c.probabilityOfProfit ?? 0) >= 55 ? C.bull : (c.probabilityOfProfit ?? 100) < 35 ? C.bear : undefined}
          hint="Integrated against the risk-neutral distribution implied by the option market's own volatility — the same distribution these premiums were priced from." />
        {/* Rounding to whole rupees destroyed exactly the number the hint calls decisive: the
            live board showed "₹1" and "₹0" on two cards whose own prose quoted 0.69 and 0.21.
            Sub-rupee expectancies are normal on a single lot and they are still the difference
            between a positive-expectancy trade and a lottery ticket. */}
        <Fig label="Expected payoff" value={fineMoney(c.expectedPayoff)}
          color={negEv ? C.bear : C.bull}
          hint="Probability-weighted average outcome, per lot. This is the number that separates a good trade from a lottery ticket with a flattering headline return — shown to the precision it actually has rather than rounded to the nearest rupee." />

        {/* WHY THIS BRANCHES.
            Return on margin, Per day and Reward:risk are all maxProfit ÷ something, so on any
            unbounded-profit structure all three are n/a at once — and the desk's output is
            dominated by long strangles and straddles, so three of nine headline figures were dead
            on most cards. A long-premium trade has its own three questions, and they are not
            these: what is actually at risk, how far price has to travel versus how far the market
            has already priced, and what holding it costs. */}
        {c.profitUnbounded ? (
          <>
            <Fig label="At risk" color={C.bear} value={money(c.maxLoss)}
              hint="The whole debit. On a long-premium structure this is what you can lose and also what you put up, so 'return on margin' has no separate meaning — which is why it is not shown here." />
            <Fig label="Move needed"
              value={c.breakEvenDistPct == null ? '—' : `${c.breakEvenDistPct}%`}
              color={c.breakEvenInsideExpectedMove === true ? C.bull : C.warn}
              hint={c.oneSdMovePct == null
                ? 'How far price must travel to the furthest break-even.'
                : `Price must travel ${c.breakEvenDistPct}% to the furthest break-even, against the ${c.oneSdMovePct}% one-sigma move the option market has already priced. Below that figure the move is cheap relative to what is implied; above it you are paying for a move larger than the market expects.`} />
            <Fig label="Decay to expiry"
              value={c.thetaPerDayPctOfMargin == null ? '—' : `${Math.round(c.thetaPerDayPctOfMargin * c.daysToExpiry)}%`}
              color={C.bear}
              hint={`Time decay per day (${c.thetaPerDayPctOfMargin ?? '—'}%) across the ${c.daysToExpiry} days to expiry, as a share of the capital at risk. This is what the position costs if the move never arrives — the real cost of being early.`} />
          </>
        ) : (
          <>
            <Fig label="Return on margin" value={c.roiPct == null ? 'n/a' : `${c.roiPct}%`}
              hint="Max profit as a percentage of the margin it ties up." />
            <Fig label="Per day" value={c.roiPerDayPct == null ? '—' : `${c.roiPerDayPct}%`}
              hint="Return on margin divided by days to expiry. For capital that gets redeployed, 6% over 2 days beats 30% over 25." />
            <Fig label="Reward : risk" value={c.riskReward == null ? 'n/a' : `${c.riskReward}`}
              hint="Max profit divided by max loss. A number below 1 is not automatically bad — it is the pairing with the chance of profit that decides." />
          </>
        )}
        <Fig label="Theta / day" value={c.thetaPerDayPctOfMargin == null ? '—' : `${c.thetaPerDayPctOfMargin}%`}
          color={(c.greeks.theta ?? 0) > 0 ? C.bull : C.bear}
          hint="Daily time decay as a percentage of margin. Positive means time is working for you." />
      </Box>

      {!compact && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.8, color: C.dim }}>
          Break-evens {c.breakEvens.join(', ') || '—'}
          {c.breakEvenDistPct != null ? ` (widest ${c.breakEvenDistPct}% from spot)` : ''}
          {c.breakEvenInsideExpectedMove === true && (
            <Typography component="span" sx={{ color: C.warn, fontWeight: 700 }}>
              {' '}— inside the move the market has already priced
            </Typography>
          )}
          {c.oneSdMovePct != null && ` · at a ±${c.oneSdMovePct}% move: ${money(c.payoffAtMinus1Sd)} / ${money(c.payoffAtPlus1Sd)}`}
        </Typography>
      )}

      {why && (
        <>
          <Divider sx={{ my: 0.8 }} />
          <Typography variant="caption" sx={{ display: 'block' }}>{why}</Typography>
        </>
      )}
      {!!risks?.length && risks.map((x, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: C.bear }}>− {x}</Typography>
      ))}
      {c.liquidityNote && (
        <Typography variant="caption" sx={{ display: 'block', color: C.warn }}>{c.liquidityNote}</Typography>
      )}
    </Box>
  );
};

export default OptionDeskPanel;
